<template>
    <transition name="modal-fade">
        <div
            v-if="visible"
            class="signature-modal-overlay"
            :class="{ 'mobile': isMobile }"
            @click.self="handleOverlayClick"
        >
            <div
                class="signature-modal"
                :class="{
                    'mobile': isMobile,
                    'landscape': isLandscape
                }"
            >
                <!-- Modal Header -->
                <div class="modal-header">
                    <h4 class="modal-title">
                        <i class="fa fa-pen-fancy"></i>
                        {{ title }}
                        <span v-if="fieldId" class="field-id">(Field #{{ fieldId }})</span>
                    </h4>
                    <button
                        class="close-btn"
                        @click="handleClose"
                        :aria-label="'Close signature modal'"
                    >
                        <i class="fa fa-times"></i>
                    </button>
                </div>

                <!-- Orientation Suggestion (Mobile Only) -->
                <div v-if="isMobile && !isLandscape" class="orientation-hint">
                    <i class="fa fa-mobile-alt"></i>
                    <span>Rotate your device for a better signing experience</span>
                </div>

                <!-- Canvas Container -->
                <div
                    class="canvas-container"
                    ref="canvasContainer"
                >
                    <VueSignaturePad
                        :width="canvasWidth + 'px'"
                        :height="canvasHeight + 'px'"
                        :options="signaturePadOptions"
                        ref="signaturePad"
                    />

                    <!-- Empty State Hint -->
                    <div v-if="!hasSignature" class="signature-hint">
                        <i class="fa fa-hand-pointer"></i>
                        <p>{{ isMobile ? 'Draw your signature with your finger' : 'Draw your signature with your mouse' }}</p>
                    </div>
                </div>

                <!-- Preview Section -->
                <div v-if="showPreview && previewData" class="preview-section">
                    <h5>Preview</h5>
                    <div class="preview-container">
                        <img :src="previewData" alt="Signature preview" />
                    </div>
                </div>

                <!-- Action Buttons -->
                <div class="modal-actions">
                    <button
                        class="btn btn-secondary btn-clear"
                        @click="clearSignature"
                        :disabled="!hasSignature"
                    >
                        <i class="fa fa-redo"></i>
                        {{ isMobile ? 'Clear' : 'Clear Signature' }}
                    </button>

                    <button
                        v-if="showPreview"
                        class="btn btn-default btn-back"
                        @click="backToDrawing"
                    >
                        <i class="fa fa-arrow-left"></i>
                        Back
                    </button>

                    <button
                        v-if="!showPreview"
                        class="btn btn-primary btn-preview"
                        @click="previewSignature"
                        :disabled="!hasSignature"
                    >
                        <i class="fa fa-eye"></i>
                        Preview
                    </button>

                    <button
                        v-if="showPreview"
                        class="btn btn-success btn-save"
                        @click="saveSignature"
                        :disabled="saving"
                    >
                        <i class="fa fa-check" v-if="!saving"></i>
                        <i class="fa fa-spinner fa-spin" v-else></i>
                        {{ saving ? 'Saving...' : 'Save Signature' }}
                    </button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
// Note: VueSignaturePad is installed globally via Vue.use() in document-signing-view.module.js

export default {
    name: 'SignatureModal',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: 'Add Your Signature'
        },
        fieldId: {
            type: [Number, String],
            default: null
        },
        preventDismiss: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            canvasWidth: 600,
            canvasHeight: 300,
            isMobile: false,
            isLandscape: false,
            hasSignature: false,
            showPreview: false,
            previewData: null,
            saving: false,
            resizeDebounceTimer: null,
            historyState: null,
            signaturePadOptions: {
                dotSize: 2,
                minWidth: 1.5,
                maxWidth: 3.5,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                penColor: 'rgba(0, 0, 0, 1)',
                velocityFilterWeight: 0.7,
                onBegin: () => {
                    this.hasSignature = true;
                },
                onEnd: () => {
                    // Signature drawing ended
                }
            }
        };
    },
    computed: {
        canvasContainerSize() {
            if (!this.$refs.canvasContainer) return { width: 600, height: 300 };
            const rect = this.$refs.canvasContainer.getBoundingClientRect();
            return {
                width: Math.floor(rect.width),
                height: Math.floor(rect.height)
            };
        }
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.onModalOpen();
            } else {
                this.onModalClose();
            }
        }
    },
    mounted() {
        this.detectDevice();
        this.calculateCanvasSize();

        // Add event listeners
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('orientationchange', this.handleOrientationChange);

        if (this.visible) {
            this.onModalOpen();
        }
    },
    beforeDestroy() {
        // Remove event listeners
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('orientationchange', this.handleOrientationChange);
        window.removeEventListener('popstate', this.handlePopState);

        // Clear debounce timer
        if (this.resizeDebounceTimer) {
            clearTimeout(this.resizeDebounceTimer);
        }
    },
    methods: {
        detectDevice() {
            // Detect mobile device
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            this.isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());

            // Detect orientation
            this.detectOrientation();
        },

        detectOrientation() {
            if (window.matchMedia) {
                this.isLandscape = window.matchMedia('(orientation: landscape)').matches;
            } else {
                this.isLandscape = window.innerWidth > window.innerHeight;
            }
        },

        calculateCanvasSize() {
            this.$nextTick(() => {
                if (this.isMobile) {
                    // Mobile: Use viewport-based dimensions
                    const vw = window.innerWidth;
                    const vh = window.innerHeight;

                    if (this.isLandscape) {
                        // Landscape: wider canvas
                        this.canvasWidth = Math.min(vw - 40, 700);
                        this.canvasHeight = Math.min(vh - 280, 350);
                    } else {
                        // Portrait: taller canvas
                        this.canvasWidth = vw - 40;
                        this.canvasHeight = Math.min(vh - 350, 300);
                    }
                } else {
                    // Desktop: Fixed comfortable size
                    this.canvasWidth = 600;
                    this.canvasHeight = 300;
                }

                // Ensure minimum sizes
                this.canvasWidth = Math.max(this.canvasWidth, 280);
                this.canvasHeight = Math.max(this.canvasHeight, 150);
            });
        },

        handleResize() {
            // Debounce resize handler for performance
            if (this.resizeDebounceTimer) {
                clearTimeout(this.resizeDebounceTimer);
            }

            this.resizeDebounceTimer = setTimeout(() => {
                this.detectOrientation();

                // Save current signature data before resize
                const currentData = this.hasSignature ? this.getSignatureData() : null;

                // Recalculate canvas size
                this.calculateCanvasSize();

                // Restore signature data after resize
                this.$nextTick(() => {
                    if (currentData && this.$refs.signaturePad) {
                        this.$refs.signaturePad.fromDataURL(currentData);
                    }
                });
            }, 150);
        },

        handleOrientationChange() {
            // Wait for orientation change to complete
            setTimeout(() => {
                this.detectOrientation();
                this.handleResize();
            }, 100);
        },

        onModalOpen() {
            // Lock body scroll on mobile
            if (this.isMobile) {
                document.body.style.overflow = 'hidden';
            }

            // Android back button handling
            this.pushHistoryState();
            window.addEventListener('popstate', this.handlePopState);

            // Reset state
            this.hasSignature = false;
            this.showPreview = false;
            this.previewData = null;
            this.saving = false;
        },

        onModalClose() {
            // Unlock body scroll
            document.body.style.overflow = '';

            // Remove popstate listener
            window.removeEventListener('popstate', this.handlePopState);
        },

        pushHistoryState() {
            // Push a dummy state for Android back button handling
            this.historyState = { modal: 'signature', timestamp: Date.now() };
            history.pushState(this.historyState, '');
        },

        handlePopState(event) {
            // Handle Android back button
            if (this.visible) {
                event.preventDefault();
                this.handleClose();
            }
        },

        handleOverlayClick() {
            this.handleClose();
        },

        handleClose() {
            if (this.preventDismiss && this.hasSignature && !this.showPreview) {
                // Confirm before closing if signature has been drawn
                if (!confirm('Are you sure you want to close? Your signature will be lost.')) {
                    // Re-push state if user cancels
                    this.pushHistoryState();
                    return;
                }
            }

            this.$emit('close');
        },

        clearSignature() {
            if (this.$refs.signaturePad) {
                this.$refs.signaturePad.clearSignature();
                this.hasSignature = false;
                this.showPreview = false;
                this.previewData = null;
            }
        },

        getSignatureData() {
            if (!this.$refs.signaturePad) return null;
            const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
            return isEmpty ? null : data;
        },

        previewSignature() {
            const data = this.getSignatureData();
            if (!data) {
                alert('Please draw your signature first.');
                return;
            }

            this.previewData = data;
            this.showPreview = true;
        },

        backToDrawing() {
            this.showPreview = false;
        },

        async saveSignature() {
            if (this.saving) return;

            const data = this.getSignatureData();
            if (!data) {
                alert('Please draw your signature first.');
                return;
            }

            this.saving = true;

            try {
                // Emit signature data as base64
                this.$emit('save', data);

                // Close modal after short delay
                setTimeout(() => {
                    this.saving = false;
                    this.$emit('close');
                }, 300);
            } catch (error) {
                console.error('Error saving signature:', error);
                this.saving = false;
                alert('Failed to save signature. Please try again.');
            }
        }
    }
};
</script>

<style scoped>
/* Overlay */
.signature-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
}

.signature-modal-overlay.mobile {
    padding: 0;
    align-items: stretch;
}

/* Modal Container */
.signature-modal {
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    max-width: 700px;
    width: 100%;
    display: flex;
    flex-direction: column;
    max-height: 90vh;
}

.signature-modal.mobile {
    border-radius: 0;
    max-width: 100%;
    max-height: 100vh;
    height: 100vh;
    width: 100vw;
}

/* Modal Header */
.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.modal-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #2d3748;
    display: flex;
    align-items: center;
    gap: 10px;
}

.modal-title i {
    color: #4299e1;
}

.field-id {
    font-size: 14px;
    color: #718096;
    font-weight: 400;
    margin-left: 8px;
}

.close-btn {
    background: none;
    border: none;
    font-size: 24px;
    color: #718096;
    cursor: pointer;
    padding: 8px;
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s;
}

.close-btn:hover {
    background-color: #f7fafc;
    color: #2d3748;
}

/* Orientation Hint */
.orientation-hint {
    background-color: #fffbeb;
    border: 1px solid #fcd34d;
    padding: 12px 16px;
    margin: 0 16px 16px 16px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #78350f;
}

.orientation-hint i {
    font-size: 20px;
    color: #f59e0b;
}

/* Canvas Container */
.canvas-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    position: relative;
    overflow: hidden;
    background-color: #f7fafc;
}

.canvas-container >>> canvas {
    border: 2px solid #cbd5e0;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    cursor: crosshair;
    touch-action: none;
}

/* Empty State Hint */
.signature-hint {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    color: #a0aec0;
    pointer-events: none;
    user-select: none;
}

.signature-hint i {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.signature-hint p {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
}

/* Preview Section */
.preview-section {
    padding: 20px 24px;
    background-color: #f7fafc;
    border-top: 1px solid #e2e8f0;
    flex-shrink: 0;
}

.preview-section h5 {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
    color: #4a5568;
}

.preview-container {
    background-color: #ffffff;
    border: 2px solid #cbd5e0;
    border-radius: 6px;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 120px;
}

.preview-container img {
    max-width: 100%;
    max-height: 150px;
    object-fit: contain;
}

/* Modal Actions */
.modal-actions {
    display: flex;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid #e2e8f0;
    background-color: #ffffff;
    flex-shrink: 0;
}

.modal-actions .btn {
    flex: 1;
    min-height: 44px;
    font-weight: 600;
    border-radius: 6px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.btn-clear {
    background-color: #f7fafc;
    border: 1px solid #cbd5e0;
    color: #4a5568;
}

.btn-clear:hover:not(:disabled) {
    background-color: #edf2f7;
}

.btn-clear:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-back {
    background-color: #ffffff;
    border: 1px solid #cbd5e0;
    color: #4a5568;
}

.btn-back:hover {
    background-color: #f7fafc;
}

.btn-preview {
    background-color: #4299e1;
    border: 1px solid #4299e1;
    color: #ffffff;
}

.btn-preview:hover:not(:disabled) {
    background-color: #3182ce;
}

.btn-preview:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-save {
    background-color: #48bb78;
    border: 1px solid #48bb78;
    color: #ffffff;
}

.btn-save:hover:not(:disabled) {
    background-color: #38a169;
}

.btn-save:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Mobile Optimizations */
@media (max-width: 768px) {
    .modal-header {
        padding: 16px;
    }

    .modal-title {
        font-size: 18px;
    }

    .canvas-container {
        padding: 12px;
    }

    .modal-actions {
        padding: 16px;
        flex-wrap: wrap;
    }

    .modal-actions .btn {
        font-size: 14px;
    }

    /* Stack buttons vertically on mobile */
    .signature-modal.mobile .modal-actions {
        flex-direction: column;
    }

    .signature-modal.mobile .modal-actions .btn {
        width: 100%;
    }
}

/* Landscape Mobile Optimizations */
.signature-modal.mobile.landscape .canvas-container {
    padding: 8px;
}

.signature-modal.mobile.landscape .modal-header {
    padding: 12px 16px;
}

.signature-modal.mobile.landscape .modal-title {
    font-size: 16px;
}

.signature-modal.mobile.landscape .modal-actions {
    padding: 12px 16px;
    flex-direction: row;
}

/* iOS Safe Area Support */
@supports (padding-top: env(safe-area-inset-top)) {
    .signature-modal.mobile {
        padding-top: env(safe-area-inset-top);
        padding-bottom: env(safe-area-inset-bottom);
    }
}

/* Transition Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease;
}

.modal-fade-enter-active .signature-modal,
.modal-fade-leave-active .signature-modal {
    transition: transform 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-fade-enter .signature-modal,
.modal-fade-leave-to .signature-modal {
    transform: scale(0.95);
}

.signature-modal.mobile.modal-fade-enter .signature-modal,
.signature-modal.mobile.modal-fade-leave-to .signature-modal {
    transform: translateY(100%);
}
</style>
